"use client";

import { createContext, useContext, useState, type ReactNode } from "react";
import { AuthDialog } from "@/components/auth-dialog";

export interface OpenAuthModalOptions {
  defaultTab?: "signin" | "signup";
  title?: string;
  description?: string;
  callbackURL?: string;
  onSuccess?: () => void;
}

export interface AuthModalContextType {
  openAuthModal: (options?: OpenAuthModalOptions) => void;
  closeAuthModal: () => void;
}

const AuthModalContext = createContext<AuthModalContextType | null>(null);

export function AuthModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<OpenAuthModalOptions>({
    defaultTab: "signup",
  });

  const openAuthModal = (opts?: OpenAuthModalOptions) => {
    setOptions({
      defaultTab: opts?.defaultTab ?? "signup",
      title: opts?.title,
      description: opts?.description,
      callbackURL: opts?.callbackURL ?? (typeof window !== "undefined" ? window.location.pathname + window.location.search : "/"),
      onSuccess: opts?.onSuccess,
    });
    setIsOpen(true);
  };

  const closeAuthModal = () => {
    setIsOpen(false);
  };

  return (
    <AuthModalContext.Provider value={{ openAuthModal, closeAuthModal }}>
      {children}
      <AuthDialog
        open={isOpen}
        onOpenChange={setIsOpen}
        defaultTab={options.defaultTab}
        title={options.title}
        description={options.description}
        callbackURL={options.callbackURL}
        onSuccess={() => {
          setIsOpen(false);
          options.onSuccess?.();
        }}
      />
    </AuthModalContext.Provider>
  );
}

export function useAuthModal() {
  const ctx = useContext(AuthModalContext);
  if (!ctx) {
    throw new Error("useAuthModal must be used within an AuthModalProvider");
  }
  return ctx;
}
